import { useEffect, useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { TYPE } from '@/lib/typography';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
}

export function SessionTimeoutModal({
  isOpen,
  remainingSeconds,
  onExtend,
  onLogout,
}: SessionTimeoutModalProps) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(remainingSeconds);

  useEffect(() => {
    setCountdown(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ds-scrim p-4">
      <div
        className="mx-auto w-full max-w-md rounded-xl bg-ds-card shadow-[var(--sh-md)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
      >
        <div className="border-b border-ds-line p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ds-amber-tint text-ds-amber-ink">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h3
                id="session-timeout-title"
                className={`${TYPE.heading} text-ds-text`}
              >
                {t('session.timeout.title', 'Session Timeout Warning')}
              </h3>
              <p className={`${TYPE.small} text-ds-muted`}>
                {t('session.timeout.subtitle', 'Your session is about to expire')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6 flex items-center justify-center gap-3 rounded-lg bg-ds-sunk p-4">
            <Clock className="h-5 w-5 text-ds-muted" />
            <div className="text-center">
              <div className={`${TYPE.display} tabular-nums text-ds-text`}>
                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </div>
              <div className={`${TYPE.caption} text-ds-muted`}>
                {t('session.timeout.remaining', 'Time remaining')}
              </div>
            </div>
          </div>

          <p className={`mb-6 ${TYPE.body} text-ds-text`}>
            {t(
              'session.timeout.message',
              'Due to inactivity, your session will expire soon. Would you like to continue working?',
            )}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row-reverse">
            <Button onClick={onExtend} className="flex-1">
              {t('session.timeout.extend', 'Continue Session')}
            </Button>
            <Button onClick={onLogout} variant="secondary" className="flex-1">
              {t('session.timeout.logout', 'Log Out Now')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
